import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopImageGalleryPage.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { Skeleton } from '../../effects';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import videoPlaceholder from '../../../assets/images/placeholder-video-thumb.svg';
import iconDownload from '../../../assets/images/icons/icon-download.svg';
import iconClose from '../../../assets/images/icons/icon-close.svg';
import {
  DEFAULT_GALLERY_PAGE_SIZE,
  getSubtypeQueryValue,
  normalizeMediaItem as normalizeGalleryMediaItem,
  fetchAlbumMediaItems,
  generateVideoThumbnail,
} from '../../Mobile/MobileImageGallery/galleryUtils';
import useDesktopScrollLock from '../DesktopPostReviewPage/useDesktopScrollLock';

const MASONRY_GUTTER = 16;
const DEFAULT_ASPECT_RATIO = 0.72;

const resolveMediaUrl = (item) => (
  item?.url ||
  item?.raw?.public_url ||
  item?.raw?.url ||
  item?.raw?.file_url ||
  item?.raw?.fileUrl ||
  placeholderImage
);

const useMedia = (queries, values, defaultValue) => {
  const match = useCallback(() => {
    const queryIndex = queries.findIndex((query) => window.matchMedia(query).matches);
    return values[queryIndex] ?? defaultValue;
  }, [queries, values, defaultValue]);

  const [value, setValue] = useState(() => (typeof window !== 'undefined' ? match() : defaultValue));

  useEffect(() => {
    const handler = () => setValue(match());
    queries.forEach((query) => window.matchMedia(query).addEventListener('change', handler));
    return () => queries.forEach((query) => window.matchMedia(query).removeEventListener('change', handler));
  }, [match, queries]);

  return value;
};

const useMeasure = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
};

const getAspectFromMetadata = (item) => {
  if (!item) return null;
  const metadata = item?.raw?.metadata || item?.raw?.meta || {};
  const dimensions = metadata.dimensions || metadata.size || {};
  const width = metadata.width || metadata.original_width || metadata.originalWidth || dimensions.width || item?.raw?.width;
  const height = metadata.height || metadata.original_height || metadata.originalHeight || dimensions.height || item?.raw?.height;
  if (!width || !height || Number(height) === 0) return null;
  return Number(width) / Number(height);
};

const useImageAspectMap = (items) => {
  const aspectRef = useRef(new Map());
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let isCancelled = false;
    const pending = items.filter((item) => item?.id && !aspectRef.current.has(item.id));
    if (!pending.length) {
      return () => {
        isCancelled = true;
      };
    }

    pending.forEach((item) => {
      const metadataAspect = getAspectFromMetadata(item);
      if (metadataAspect) {
        aspectRef.current.set(item.id, metadataAspect);
        setVersion((prev) => prev + 1);
        return;
      }
      const isVideo = item?.mediaType === 'video' || item?.raw?.media_type === 'video';
      if (isVideo) {
        aspectRef.current.set(item.id, DEFAULT_ASPECT_RATIO);
        setVersion((prev) => prev + 1);
        return;
      }

      const image = new Image();
      const src = resolveMediaUrl(item);
      image.src = src;
      image.onload = () => {
        if (isCancelled) return;
        const width = image.naturalWidth || image.width || 0;
        const height = image.naturalHeight || image.height || 0;
        const aspect = width && height ? width / height : DEFAULT_ASPECT_RATIO;
        aspectRef.current.set(item.id, aspect);
        setVersion((prev) => prev + 1);
      };
      image.onerror = () => {
        if (isCancelled) return;
        aspectRef.current.set(item.id, DEFAULT_ASPECT_RATIO);
        setVersion((prev) => prev + 1);
      };
    });

    return () => {
      isCancelled = true;
    };
  }, [items]);

  const getAspect = useCallback((item) => {
    if (!item) return DEFAULT_ASPECT_RATIO;
    const cached = aspectRef.current.get(item.id);
    if (cached) {
      return cached;
    }
    const metadataAspect = getAspectFromMetadata(item);
    if (metadataAspect) {
      return metadataAspect;
    }
    return DEFAULT_ASPECT_RATIO;
  }, []);

  return { getAspect, version };
};

const BackIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = ({ size = 18, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const resolveCreationDate = (item) => {
  if (!item) return null;
  return (
    item?.uploadedAt ||
    item?.createdAt ||
    item?.raw?.created_at ||
    item?.raw?.createdAt ||
    item?.raw?.uploaded_at ||
    item?.raw?.uploadedAt ||
    item?.raw?.metadata?.created_at ||
    item?.raw?.metadata?.createdAt ||
    null
  );
};

const DesktopGalleryThumbnail = React.memo(({ item, onSelect, formatCreationDate, thumbnailUrl }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isVideo = item?.mediaType === 'video' || item?.raw?.media_type === 'video';

  const resolvedUrl = useMemo(() => {
    return resolveMediaUrl(item);
  }, [item]);
  const resolvedThumbnailUrl = thumbnailUrl || (!isVideo ? resolvedUrl : null) || (isVideo ? videoPlaceholder : placeholderImage);

  const creationDate = useMemo(() => resolveCreationDate(item), [item]);
  const creationLabel = useMemo(() => (
    typeof formatCreationDate === 'function'
      ? formatCreationDate(creationDate)
      : ''
  ), [creationDate, formatCreationDate]);
  const displayUrl = isVideo ? (thumbnailUrl || videoPlaceholder) : resolvedUrl;

  const handleVideoMetadata = useCallback((event) => {
    const video = event.currentTarget;
    if (!video || typeof video.currentTime !== 'number') {
      return;
    }
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = duration > 0 ? Math.min(0.1, Math.max(0, duration - 0.1)) : 0.1;
    if (Number.isFinite(targetTime) && targetTime > 0 && video.currentTime !== targetTime) {
      try {
        video.currentTime = targetTime;
      } catch (error) {
        // Ignore seek failures for thumbnails.
      }
    }
  }, []);

  const handleClick = useCallback(() => {
    if (typeof onSelect === 'function') {
      onSelect({
        ...item,
        displayUrl,
        selectedUrl: resolvedUrl,
      });
    }
  }, [displayUrl, item, onSelect, resolvedUrl]);

  const handleError = useCallback((event) => {
    if (!hasError) {
      setHasError(true);
      if (event.currentTarget?.tagName === 'IMG') {
        event.currentTarget.onerror = null;
        event.currentTarget.src = placeholderImage;
      }
    }
  }, [hasError]);

  const shouldRenderVideo = isVideo && !thumbnailUrl && !hasError && resolvedUrl && resolvedUrl !== placeholderImage;

  return (
    <button
      type="button"
      className={`loopino-desktop-gallery-item ${isLoaded ? 'is-loaded' : ''}`}
      onClick={handleClick}
    >
      <div className="loopino-desktop-gallery-thumb">
        {!isLoaded && (
          <div className="loopino-desktop-gallery-skeleton">
            <Skeleton variant="rounded" width="100%" height="100%" shimmer />
          </div>
        )}
        {shouldRenderVideo ? (
          <video
            src={resolvedUrl}
            poster={videoPlaceholder}
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setIsLoaded(true)}
            onLoadedMetadata={handleVideoMetadata}
            onError={handleError}
          />
        ) : (
          <img
            src={hasError ? placeholderImage : resolvedThumbnailUrl}
            alt={item?.name || 'Media'}
            onLoad={() => setIsLoaded(true)}
            onError={handleError}
            loading="lazy"
            decoding="async"
          />
        )}
        <div className="loopino-desktop-gallery-meta-overlay">
          <p className="loopino-desktop-gallery-card-title">{creationLabel}</p>
          <span className="loopino-desktop-gallery-tag">{item?.type || 'image'}</span>
        </div>
      </div>
    </button>
  );
});

const DesktopMasonryGrid = ({ items, onSelect, formatCreationDate, thumbnailMap }) => {
  const columns = useMedia(
    ['(min-width:1500px)', '(min-width:1100px)', '(min-width:800px)', '(min-width:600px)'],
    [5, 4, 3, 2],
    1
  );
  const [containerRef, { width }] = useMeasure();
  const { getAspect, version } = useImageAspectMap(items);

  const layout = useMemo(() => {
    if (!width) {
      return { positioned: [], height: 0 };
    }

    const columnCount = Math.max(1, columns || 1);
    const gutter = MASONRY_GUTTER;
    const columnWidth = Math.max(220, (width - gutter * (columnCount - 1)) / columnCount);
    const columnHeights = new Array(columnCount).fill(0);

    const positioned = items.map((item) => {
      const aspect = getAspect(item) || DEFAULT_ASPECT_RATIO;
      const height = columnWidth / Math.max(aspect, 0.2);

      const columnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      const x = columnIndex * (columnWidth + gutter);
      const y = columnHeights[columnIndex];
      columnHeights[columnIndex] += height + gutter;

      return {
        id: item.id,
        item,
        width: columnWidth,
        height,
        x,
        y,
      };
    });

    const height = columnHeights.length ? Math.max(...columnHeights) - gutter : 0;
    return {
      positioned,
      height: Math.max(0, height),
    };
  }, [columns, getAspect, items, version, width]);

  const containerHeight = layout.height || (items.length ? 400 : 0);

  return (
    <div ref={containerRef} className="loopino-desktop-masonry" style={{ height: containerHeight }}>
      {layout.positioned.map((entry) => (
        <div
          key={entry.id}
          className="loopino-desktop-masonry-item"
          style={{
            transform: `translate(${entry.x}px, ${entry.y}px)`,
            width: entry.width,
            height: entry.height,
          }}
        >
          <DesktopGalleryThumbnail
            item={entry.item}
            onSelect={onSelect}
            formatCreationDate={formatCreationDate}
            thumbnailUrl={thumbnailMap?.[entry.item?.id ? `${entry.item.id}` : '']}
          />
        </div>
      ))}
    </div>
  );
};

const DesktopImageGalleryPage = ({ onClose, onSelect, viewerOrigin = 'desktop', mode = 'embedded' }) => {
  const { get } = useApi();
  const { t, language } = useLanguage();
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMediaType, setSelectedMediaType] = useState('all');
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [mediaItems, setMediaItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [galleryError, setGalleryError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [viewerItem, setViewerItem] = useState(null);
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [isAlbumDropdownOpen, setIsAlbumDropdownOpen] = useState(false);
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [videoThumbnailsLoading, setVideoThumbnailsLoading] = useState({});

  const loadMoreRef = useRef(null);
  const albumMediaCacheRef = useRef(new Map());
  const activeRequestRef = useRef(null);
  const albumDropdownRef = useRef(null);
  const isStandalone = mode === 'standalone';

  const normalizeMediaItem = useCallback((item) => {
    return normalizeGalleryMediaItem(item, t);
  }, [t]);

  const resolveMediaSourceUrl = useCallback((item) => {
    if (!item) return null;
    const rawSource = item.raw || {};
    return (
      rawSource.public_url ||
      rawSource.url ||
      rawSource.file_url ||
      rawSource.fileUrl ||
      item.url ||
      null
    );
  }, []);

  const resolveThumbnailKey = useCallback((item) => {
    const rawKey = item?.id || resolveMediaSourceUrl(item);
    return rawKey ? `${rawKey}` : null;
  }, [resolveMediaSourceUrl]);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      console.warn('[DesktopImageGallery] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
  }, [language]);

  const formatCreationDate = useCallback((value) => {
    const fallback = t('imageGallery.meta.noDate', { defaultValue: 'Data non disponibile' });
    if (!value) return fallback;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return fallback;
    }
    return dateFormatter.format(parsed);
  }, [dateFormatter, t]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (viewerItem) {
          setIsViewerVisible(false);
          setViewerItem(null);
        } else if (!isStandalone && typeof onClose === 'function') {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isStandalone, onClose, viewerItem]);

  useEffect(() => {
    let isCancelled = false;

    const loadAlbums = async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          page_size: '50',
          include_archived: 'false',
        });

        const response = await get(`/api/v1/media/albums?${params.toString()}`);
        if (isCancelled) return;

        const rawItems = Array.isArray(response?.items) ? response.items : [];
        const normalizedAlbums = rawItems
          .map((album) => {
            const payload = album?.album || album?.data?.album || album;
            const id = payload?.id || payload?._id || payload?.album_id || payload?.albumId;
            if (!id) return null;
            return {
              id,
              title: payload.title || payload.name || payload.metadata?.slug || t('imageGallery.filters.album.untitled'),
              metadata: payload.metadata || {},
            };
          })
          .filter(Boolean);

        setAlbums(normalizedAlbums);
        albumMediaCacheRef.current.clear();
      } catch (error) {
        if (!isCancelled) {
          console.error('[DesktopImageGallery] Failed to load albums', error);
        }
      }
    };

    loadAlbums();
    return () => {
      isCancelled = true;
    };
  }, [get, t]);

  const loadPage = useCallback(async (pageToLoad = 1) => {
    const isInitialLoad = pageToLoad === 1;
    const requestToken = Symbol('desktop-gallery-load');
    activeRequestRef.current = requestToken;

    setGalleryError(null);

    if (isInitialLoad) {
      setIsLoading(true);
      setIsFetchingMore(false);
      setHasMore(true);
      setPagination({ page: 1, totalPages: 1 });
    } else {
      setIsFetchingMore(true);
    }

    try {
      if (selectedAlbum && selectedAlbum !== 'all') {
        let normalizedAlbumMedia = albumMediaCacheRef.current.get(selectedAlbum);

        if (!normalizedAlbumMedia) {
          const albumMediaItems = await fetchAlbumMediaItems({ get, albumId: selectedAlbum });
          normalizedAlbumMedia = albumMediaItems.map((item) => normalizeMediaItem(item)).filter(Boolean);
          albumMediaCacheRef.current.set(selectedAlbum, normalizedAlbumMedia);
        }

        const filteredAlbumMedia = normalizedAlbumMedia.filter((item) => {
          const matchesSubtype = selectedType === 'all' || item.type === selectedType;
          const matchesMediaType = selectedMediaType === 'all' || item.mediaType === selectedMediaType;
          return matchesSubtype && matchesMediaType;
        });

        const totalPages = Math.ceil(filteredAlbumMedia.length / DEFAULT_GALLERY_PAGE_SIZE) || 1;
        const startIndex = (pageToLoad - 1) * DEFAULT_GALLERY_PAGE_SIZE;
        const endIndex = startIndex + DEFAULT_GALLERY_PAGE_SIZE;
        const pageItems = filteredAlbumMedia.slice(startIndex, endIndex);

        if (activeRequestRef.current !== requestToken) return;

        setMediaItems((prev) => (isInitialLoad ? pageItems : [...prev, ...pageItems]));
        setPagination({
          page: pageToLoad,
          totalPages,
          totalItems: filteredAlbumMedia.length,
        });
        setHasMore(pageToLoad < totalPages);
        return;
      }

      const params = new URLSearchParams({
        page: String(pageToLoad),
        page_size: String(DEFAULT_GALLERY_PAGE_SIZE),
      });

      if (selectedMediaType && selectedMediaType !== 'all') {
        params.set('media_type', selectedMediaType);
      }

      const subtypeValue = getSubtypeQueryValue(selectedType);
      if (subtypeValue) {
        params.set('subtype', subtypeValue);
      }

      const response = await get(`/api/v1/media/files?${params.toString()}`);
      const items = Array.isArray(response?.items) ? response.items : [];
      const normalized = items
        .map((entry) => normalizeMediaItem(entry?.media_file || entry?.media || entry))
        .filter(Boolean);

      if (activeRequestRef.current !== requestToken) return;

      setMediaItems((prev) => (isInitialLoad ? normalized : [...prev, ...normalized]));

      const paginationInfo = response?.pagination || {};
      const totalPages = paginationInfo.total_pages || paginationInfo.totalPages || (() => {
        const totalItems = paginationInfo.total_items || paginationInfo.totalItems;
        if (typeof totalItems === 'number') {
          const pageSizeValue = paginationInfo.page_size || paginationInfo.pageSize || DEFAULT_GALLERY_PAGE_SIZE;
          return Math.max(1, Math.ceil(totalItems / pageSizeValue));
        }
        if (normalized.length < DEFAULT_GALLERY_PAGE_SIZE) {
          return pageToLoad;
        }
        return pageToLoad + 1;
      })();

      const hasNextPage = (() => {
        if (paginationInfo.total_pages || paginationInfo.totalPages) {
          return pageToLoad < totalPages;
        }
        return normalized.length === DEFAULT_GALLERY_PAGE_SIZE;
      })();

      setPagination({
        page: pageToLoad,
        totalPages,
        totalItems: paginationInfo.total_items || paginationInfo.totalItems || null,
      });
      setHasMore(hasNextPage);
    } catch (error) {
      if (activeRequestRef.current !== requestToken) return;

      console.error('[DesktopImageGallery] Failed to load media list', error);
      setGalleryError(error.message || t('imageGallery.states.error.defaultMessage'));
      if (pageToLoad === 1) {
        setMediaItems([]);
      }
      setHasMore(false);
    } finally {
      if (activeRequestRef.current === requestToken) {
        if (isInitialLoad) {
          setIsLoading(false);
        } else {
          setIsFetchingMore(false);
          setIsLoading(false);
        }
      }
    }
  }, [get, normalizeMediaItem, selectedAlbum, selectedMediaType, selectedType, t]);

  useEffect(() => {
    setMediaItems([]);
    setPagination({ page: 1, totalPages: 1 });
    setHasMore(true);
    setGalleryError(null);
    loadPage(1);
  }, [selectedAlbum, selectedMediaType, selectedType, loadPage]);

  useEffect(() => {
    let isCancelled = false;
    const candidates = mediaItems.filter((item) => item?.mediaType === 'video');

    if (candidates.length === 0) {
      return () => {
        isCancelled = true;
      };
    }

    const generateThumbnails = async () => {
      for (const item of candidates) {
        if (isCancelled) {
          return;
        }
        const key = resolveThumbnailKey(item);
        if (!key || videoThumbnails[key] || videoThumbnailsLoading[key]) {
          continue;
        }

        setVideoThumbnailsLoading((prev) => ({ ...prev, [key]: true }));
        const sourceUrl = resolveMediaSourceUrl(item);
        if (!sourceUrl) {
          setVideoThumbnailsLoading((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          continue;
        }

        try {
          const thumbnail = await generateVideoThumbnail(sourceUrl, { width: 240, quality: 0.8 });
          if (!isCancelled && thumbnail) {
            setVideoThumbnails((prev) => ({ ...prev, [key]: thumbnail }));
          }
        } catch (error) {
          if (!isCancelled) {
            console.error('[DesktopImageGallery] Failed to generate video thumbnail', error);
          }
        } finally {
          if (!isCancelled) {
            setVideoThumbnailsLoading((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }
        }
      }
    };

    generateThumbnails();

    return () => {
      isCancelled = true;
    };
  }, [
    mediaItems,
    resolveMediaSourceUrl,
    resolveThumbnailKey,
    videoThumbnails,
    videoThumbnailsLoading,
  ]);

  const handleRefresh = useCallback(() => {
    setMediaItems([]);
    setPagination({ page: 1, totalPages: 1 });
    setHasMore(true);
    setGalleryError(null);
    loadPage(1);
  }, [loadPage]);

  const loadNextPage = useCallback(() => {
    if (isLoading || isFetchingMore || !hasMore) return;
    const nextPage = (pagination.page || 1) + 1;
    loadPage(nextPage);
  }, [hasMore, isFetchingMore, isLoading, loadPage, pagination.page]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return () => {};

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        loadNextPage();
      }
    }, {
      root: null,
      rootMargin: '320px 0px',
      threshold: 0.1,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadNextPage]);

  const albumOptions = useMemo(() => ([
    { value: 'all', label: t('imageGallery.filters.album.all') },
    ...albums.map((album) => ({
      value: album.id,
      label: album.title || t('imageGallery.filters.album.untitled'),
    })),
  ]), [albums, t]);

  const mediaTypeOptions = useMemo(() => ([
    { value: 'all', label: t('imageGallery.filters.mediaType.all') },
    { value: 'image', label: t('imageGallery.filters.mediaType.image') },
    { value: 'video', label: t('imageGallery.filters.mediaType.video') },
  ]), [t]);

  const typeOptions = useMemo(() => ([
    { value: 'all', label: t('imageGallery.filters.type.all') },
    { value: 'uploaded', label: t('imageGallery.filters.type.uploaded') },
    { value: 'generated', label: t('imageGallery.filters.type.generated') },
  ]), [t]);

  const handleSelectType = useCallback((value) => {
    setSelectedType(value);
  }, []);

  const handleSelectMediaType = useCallback((value) => {
    setSelectedMediaType(value);
  }, []);

  const handleSelectAlbum = useCallback((value) => {
    setSelectedAlbum(value);
  }, []);

  useEffect(() => {
    if (!isAlbumDropdownOpen || typeof document === 'undefined') return () => {};
    const handleClickOutside = (event) => {
      const container = albumDropdownRef.current;
      if (container && !container.contains(event.target)) {
        setIsAlbumDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsAlbumDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAlbumDropdownOpen]);

  const handleThumbSelect = useCallback((item) => {
    setViewerItem(item);
    setIsViewerVisible(true);
  }, []);

  const handleViewerClose = useCallback(() => {
    setIsViewerVisible(false);
    setViewerItem(null);
  }, []);

  const handleViewerDownload = useCallback(() => {
    if (!viewerItem) return;
    const resolvedUrl = viewerItem.selectedUrl || viewerItem.displayUrl || viewerItem.url;
    if (!resolvedUrl) return;

    const trimmedName = typeof viewerItem.name === 'string' ? viewerItem.name.trim() : '';
    let filename = trimmedName;
    if (!filename) {
      try {
        const url = new URL(resolvedUrl, window.location.origin);
        filename = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || 'download');
      } catch (error) {
        filename = 'download';
      }
    }

    const link = document.createElement('a');
    link.href = resolvedUrl;
    link.download = filename || 'download';
    link.rel = 'noopener';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [viewerItem]);

  const handleViewerBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget) {
      handleViewerClose();
    }
  }, [handleViewerClose]);

  const handleUseImage = useCallback(() => {
    if (isStandalone || typeof onSelect !== 'function' || !viewerItem) return;
    const resolvedUrl = viewerItem.selectedUrl || viewerItem.displayUrl || viewerItem.url;
    onSelect({
      ...viewerItem,
      viewerOrigin,
      selectedUrl: resolvedUrl,
      displayUrl: viewerItem.displayUrl || viewerItem.url || resolvedUrl,
    });
    handleViewerClose();
    if (!isStandalone && typeof onClose === 'function') {
      onClose();
    }
  }, [handleViewerClose, isStandalone, onClose, onSelect, viewerItem, viewerOrigin]);

  useDesktopScrollLock(isViewerVisible, 'desktop-gallery-viewer');

  const isInitialLoading = isLoading && mediaItems.length === 0;
  const hasMedia = mediaItems.length > 0;
  const isViewerVideo = viewerItem?.mediaType === 'video' || viewerItem?.raw?.media_type === 'video';
  const viewerUrl = viewerItem?.selectedUrl || viewerItem?.displayUrl || viewerItem?.url || placeholderImage;
  const viewerDownloadUrl = viewerItem?.selectedUrl || viewerItem?.displayUrl || viewerItem?.url;
  const viewerTitle = t('imageGallery.viewer.defaultTitle', {
    defaultValue: isViewerVideo ? 'Anteprima video' : 'Anteprima immagine',
  });
  const viewerSelectLabel = t('imageGallery.viewer.selectButton', {
    defaultValue: isViewerVideo ? 'Usa video' : 'Usa immagine',
  });
  const handleViewerVideoMetadata = useCallback((event) => {
    const video = event.currentTarget;
    if (!video || typeof video.currentTime !== 'number') {
      return;
    }
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = duration > 0 ? Math.min(0.1, Math.max(0, duration - 0.1)) : 0.1;
    if (Number.isFinite(targetTime) && targetTime > 0 && video.currentTime !== targetTime) {
      try {
        video.currentTime = targetTime;
      } catch (error) {
        // Ignore seek failures for preview frame.
      }
    }
  }, []);
  const selectedAlbumLabel = useMemo(() => {
    return albumOptions.find((option) => option.value === selectedAlbum)?.label || albumOptions[0]?.label || '';
  }, [albumOptions, selectedAlbum]);

  return (
    <div className="loopino-desktop-shell loopino-desktop-gallery-shell">
      <header className="loopino-desktop-post-review-header loopino-desktop-gallery-page-header">
        <div className="loopino-desktop-gallery-header-block">
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <div className="loopino-desktop-gallery-title-row">
            <h1>{t('imageGallery.desktop.title', { defaultValue: 'Galleria AI' })}</h1>
          </div>
          <p className="loopino-desktop-post-review-summary">
            {t('imageGallery.header.helper', { defaultValue: 'Sfoglia, filtra e scegli immagini caricate o generate dall\'AI per usarle ovunque nel desktop.' })}
          </p>
        </div>
        <div className="loopino-desktop-gallery-actions-inline">
          {!isStandalone && typeof onClose === 'function' && (
            <button
              type="button"
              className="loopino-desktop-post-nav"
              onClick={onClose}
              aria-label={t('common.actions.back', { defaultValue: 'Torna indietro' })}
            >
              <BackIcon size={18} />
            </button>
          )}
          <button
            type="button"
            className="loopino-desktop-post-refresh"
            onClick={handleRefresh}
          >
            {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
          </button>
        </div>
      </header>

      <div className="loopino-desktop-gallery-content">
        <div className="loopino-desktop-gallery-filter-bar">
          <div className="loopino-desktop-gallery-filter-group">
            {typeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`loopino-desktop-gallery-filter ${selectedType === option.value ? 'is-active' : ''}`}
                onClick={() => handleSelectType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="loopino-desktop-gallery-filter-group">
            {mediaTypeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`loopino-desktop-gallery-filter ${selectedMediaType === option.value ? 'is-active' : ''}`}
                onClick={() => handleSelectMediaType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="loopino-desktop-gallery-filter-group loopino-desktop-gallery-filter-group-album">
            <div
              className="loopino-dna-dropdown loopino-desktop-gallery-album-dropdown"
              ref={albumDropdownRef}
              data-open={isAlbumDropdownOpen || undefined}
            >
              <button
                type="button"
                className="loopino-dna-dropdown-trigger"
                onClick={() => setIsAlbumDropdownOpen((prev) => !prev)}
                aria-expanded={isAlbumDropdownOpen}
                aria-haspopup="listbox"
              >
                <span className="loopino-desktop-gallery-album-trigger-text">
                  <span className="loopino-desktop-gallery-album-trigger-prefix">
                    {t('imageGallery.filters.album.label', { defaultValue: 'Album' })}
                  </span>
                  <span className="loopino-desktop-gallery-album-trigger-value">
                    {selectedAlbumLabel}
                  </span>
                </span>
                <ChevronDownIcon className="loopino-desktop-gallery-album-chevron" />
              </button>
              {isAlbumDropdownOpen && (
                <div className="loopino-dna-dropdown-menu" role="listbox">
                  {albumOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className="loopino-dna-dropdown-item"
                      data-selected={selectedAlbum === option.value || undefined}
                      aria-selected={selectedAlbum === option.value}
                      role="option"
                      onClick={() => {
                        handleSelectAlbum(option.value);
                        setIsAlbumDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="loopino-desktop-gallery-grid">
          {galleryError && (
            <div className="loopino-desktop-gallery-empty">
              <p>{t('imageGallery.states.error.title')}</p>
              <span>{galleryError}</span>
            </div>
          )}

          {!galleryError && isInitialLoading && (
            <div className="loopino-desktop-masonry-skeletons">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={`gallery-skeleton-${index}`}
                  className="loopino-desktop-masonry-skeleton"
                  style={{ height: 180 + (index % 5) * 46 }}
                >
                  <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                </div>
              ))}
            </div>
          )}

          {!galleryError && hasMedia && (
            <DesktopMasonryGrid
              items={mediaItems}
              onSelect={handleThumbSelect}
              formatCreationDate={formatCreationDate}
              thumbnailMap={videoThumbnails}
            />
          )}

          {!galleryError && !hasMedia && !isInitialLoading && (
            <div className="loopino-desktop-gallery-empty">
              <p>{t('imageGallery.states.empty.title')}</p>
              <span>{t('imageGallery.states.empty.subtitle')}</span>
            </div>
          )}
        </div>

        <div ref={loadMoreRef} className="loopino-desktop-gallery-sentinel">
          {isFetchingMore && (
            <div className="loopino-desktop-gallery-loading-more">
              <Skeleton variant="rounded" width={140} height={12} />
            </div>
          )}
        </div>
      </div>

      {(viewerItem || isViewerVisible) && (
        <div
          className={`loopino-desktop-gallery-viewer ${isViewerVisible ? 'is-visible' : ''}`}
          onClick={handleViewerBackdrop}
          role="dialog"
          aria-modal="true"
        >
          <div className="loopino-desktop-gallery-viewer-card" onClick={(event) => event.stopPropagation()}>
            <div className="loopino-desktop-gallery-viewer-header">
              <div>
                <p className="loopino-desktop-gallery-eyebrow">{viewerTitle}</p>
                <h4>{viewerItem?.name || viewerTitle}</h4>
              </div>
              <div className="loopino-desktop-gallery-viewer-actions">
                {!isStandalone && typeof onSelect === 'function' && (
                  <button type="button" className="loopino-button" data-variant="primary" onClick={handleUseImage}>
                    {viewerSelectLabel}
                  </button>
                )}
                <button
                  type="button"
                  className="loopino-button"
                  data-variant="ghost"
                  onClick={handleViewerDownload}
                  aria-label={t('imageGallery.viewer.downloadButton', { defaultValue: 'Scarica' })}
                  disabled={!viewerDownloadUrl}
                >
                  <img src={iconDownload} alt="" aria-hidden="true" className="loopino-desktop-gallery-action-icon" />
                </button>
                <button
                  type="button"
                  className="loopino-button"
                  data-variant="ghost"
                  onClick={handleViewerClose}
                  aria-label={t('imageGallery.viewer.closeButton', { defaultValue: 'Chiudi' })}
                >
                  <img src={iconClose} alt="" aria-hidden="true" className="loopino-desktop-gallery-action-icon" />
                </button>
              </div>
            </div>
            <div className="loopino-desktop-gallery-viewer-body">
              {isViewerVideo ? (
                <video
                  src={viewerUrl}
                  controls
                  preload="auto"
                  poster={videoPlaceholder}
                  onLoadedMetadata={handleViewerVideoMetadata}
                />
              ) : (
                <img src={viewerUrl} alt={viewerItem?.name || 'Selezione'} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopImageGalleryPage;
